import React from 'react';
import { Box, Typography } from '@mui/material';

interface LogoProps {
  size?: 'small' | 'medium' | 'large';
  showText?: boolean;
  variant?: 'full' | 'icon-only' | 'text-only';
  color?: 'primary' | 'secondary' | 'inherit';
}

const Logo: React.FC<LogoProps> = ({ 
  size = 'medium', 
  showText = true, 
  variant = 'full',
  color = 'primary'
}) => {
  const getSizeConfig = () => {
    switch (size) {
      case 'small':
        return { iconSize: 32, fontSize: 'h6', spacing: 1 };
      case 'large':
        return { iconSize: 80, fontSize: 'h3', spacing: 2 };
      default: // medium
        return { iconSize: 48, fontSize: 'h5', spacing: 1.5 };
    }
  };

  const { iconSize, fontSize, spacing } = getSizeConfig();

  const LogoIcon = () => (
    <Box
      sx={{
        width: iconSize,
        height: iconSize,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
        border: '3px solid #e0e0e0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
        '&::before': {
          content: '""',
          position: 'absolute',
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
        }
      }}
    >
      {/* Stylized 'S' with needle and thread */}
      <Box
        sx={{
          position: 'relative',
          width: iconSize * 0.6,
          height: iconSize * 0.6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Main 'S' shape */}
        <Box
          sx={{
            position: 'absolute',
            width: iconSize * 0.4,
            height: iconSize * 0.4,
            border: `3px solid #e0e0e0`,
            borderRadius: '50%',
            borderTop: 'none',
            borderRight: 'none',
            transform: 'rotate(-45deg)',
          }}
        />
        
        {/* Needle */}
        <Box
          sx={{
            position: 'absolute',
            width: '2px',
            height: iconSize * 0.3,
            backgroundColor: '#e0e0e0',
            borderRadius: '1px',
            transform: 'rotate(45deg)',
            top: iconSize * 0.1,
            left: iconSize * 0.25,
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '-3px',
              left: '-2px',
              width: '6px',
              height: '6px',
              backgroundColor: '#e0e0e0',
              borderRadius: '50%',
            }
          }}
        />
        
        {/* Thread */}
        <Box
          sx={{
            position: 'absolute',
            width: '1px',
            height: iconSize * 0.2,
            backgroundColor: '#e0e0e0',
            borderRadius: '0.5px',
            transform: 'rotate(45deg)',
            top: iconSize * 0.15,
            left: iconSize * 0.2,
          }}
        />
        
        {/* Thimble */}
        <Box
          sx={{
            position: 'absolute',
            width: iconSize * 0.15,
            height: iconSize * 0.1,
            backgroundColor: '#e0e0e0',
            borderRadius: '50% 50% 0 0',
            bottom: iconSize * 0.1,
            left: iconSize * 0.1,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '2px',
              left: '2px',
              right: '2px',
              bottom: '2px',
              backgroundColor: '#1976d2',
              borderRadius: '50% 50% 0 0',
            }
          }}
        />
        
        {/* Gear icon */}
        <Box
          sx={{
            position: 'absolute',
            top: iconSize * 0.05,
            right: iconSize * 0.05,
            width: iconSize * 0.15,
            height: iconSize * 0.15,
            backgroundColor: '#ffc107',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&::before': {
              content: '""',
              position: 'absolute',
              width: '80%',
              height: '80%',
              border: '2px solid #ffc107',
              borderRadius: '50%',
              background: 'conic-gradient(from 0deg, #ffc107 0deg, #ffc107 60deg, transparent 60deg, transparent 120deg, #ffc107 120deg, #ffc107 180deg, transparent 180deg, transparent 240deg, #ffc107 240deg, #ffc107 300deg, transparent 300deg, transparent 360deg)',
            }
          }}
        />
        
        {/* Mouse pointer */}
        <Box
          sx={{
            position: 'absolute',
            bottom: iconSize * 0.05,
            right: iconSize * 0.1,
            width: 0,
            height: 0,
            borderLeft: `${iconSize * 0.08}px solid transparent`,
            borderRight: `${iconSize * 0.08}px solid transparent`,
            borderBottom: `${iconSize * 0.1}px solid #ffc107`,
            transform: 'rotate(45deg)',
          }}
        />
      </Box>
    </Box>
  );

  const LogoText = () => (
    <Box>
      <Typography
        variant={fontSize as any}
        component="div"
        sx={{
          fontWeight: 'bold',
          color: color === 'primary' ? 'primary.main' : color === 'secondary' ? 'secondary.main' : 'inherit',
          lineHeight: 1,
        }}
      >
        TAILOR STITCH
        <Box
          component="span"
          sx={{
            color: '#ffc107',
            ml: 0.5,
          }}
        >
          Pro
        </Box>
      </Typography>
      {showText && (
        <Typography
          variant="caption"
          sx={{
            color: '#ffc107',
            fontSize: '0.7rem',
            fontWeight: 500,
            display: 'block',
            mt: 0.5,
            transform: 'translateX(2px)',
          }}
        >
          SMART STITCHING MANAGEMENT
        </Typography>
      )}
    </Box>
  );

  if (variant === 'icon-only') {
    return null; // No icon to show
  }

  if (variant === 'text-only') {
    return <LogoText />;
  }

  // Always show text only (no icon)
  return <LogoText />;
};

export default Logo;
